import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import {
  ClipboardList,
  Clock3,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Filter,
  LucideAngularModule,
  Sparkles,
  Download,
  CalendarDays,
  FileText,
  Circle,
  Plus,
  ChartLine,
  FileClock,
} from 'lucide-angular';
import { DEFAULT_PAGE_SIZE_OPTIONS } from '../../../core/constants/pagination.constants';
import { Contract, Vendor } from '../../../core/models/domain.models';
import { ApiService } from '../../../core/services/api.service';
import { AuthService } from '../../../core/services/auth.service';
import { ContractsService } from '../../contract/services/contracts.service';
import { VendorsService } from '../../vendor/services/vendors.service';
import { ToastService } from '../../../shared/ui/toast.service';
import { displayApplicationType, normalizeApplicationType } from '../application-type.util';

type ApplicationStatus = 'approved' | 'pending' | 'rejected' | 'punched';
type ApplicationTab = 'pendingPunch' | 'monthlyAdvance' | 'all';
type ApplicationStatusFilter = 'all' | 'approved' | 'pending' | 'rejected';
type ApplicationKindFilter = 'all' | 'salePurchase' | 'banking' | 'hybrid';

const MW_PER_CONTRACT = 700;

interface ApplicationScheduleApi {
  id: string;
  application_id: string;
  from_date: string;
  to_date: string;
  from_time: string;
  to_time: string;
  quantum?: number | null;
}

interface ApplicationApi {
  id: string;
  application_number: string;
  contract_id: string;
  application_type: string;
  application_date: string;
  route?: string | null;
  seller_vendor_id?: string | null;
  buyer_vendor_id?: string | null;
  total_quantum?: number | null;
  approved_quantum?: number | string | null;
  status: string;
  schedules?: ApplicationScheduleApi[];
}

interface ApplicationRecord {
  id: string;
  appNo: string;
  contractNo: string;
  appType: string;
  applicationDate: string;
  buyer: string;
  buyerVendorId: string;
  buyerSldc: string;
  buyerRldc: string;
  seller: string;
  sellerVendorId: string;
  sellerSldc: string;
  sellerRldc: string;
  sellerRegistrationCode: string;
  quantum: number;
  corridor: string[];
  validFrom: string;
  validTo: string;
  validFromRaw: string;
  validToRaw: string;
  hoursFrom: string;
  hoursTo: string;
  status: ApplicationStatus;
}

interface PendingPunchRow {
  contractId: string;
  contractNo: string;
  applicationCount: number;
  applicationNumbers: string[];
  buyerParties: Array<{ vendorId: string; name: string }>;
  sellerParties: Array<{ vendorId: string; name: string }>;
  type: string;
  period: string;
  quantum: number;
  alreadyScheduled: number;
  remainingToPunch: number;
}

@Component({
  standalone: true,
  imports: [CommonModule, LucideAngularModule, RouterLink],
  templateUrl: './application-list.page.html',
  styleUrls: ['./application-list.page.scss'],
})
export class ApplicationListPage implements OnInit {
  protected readonly router = inject(Router);
  private readonly api = inject(ApiService);
  private readonly auth = inject(AuthService);
  private readonly contractsService = inject(ContractsService);
  private readonly vendorsService = inject(VendorsService);
  private readonly toast = inject(ToastService);

  protected readonly ClipboardListIcon = ClipboardList;
  protected readonly Clock3Icon = Clock3;
  protected readonly FilterIcon = Filter;
  protected readonly SparklesIcon = Sparkles;
  protected readonly DownloadIcon = Download;
  protected readonly CalendarDaysIcon = CalendarDays;
  protected readonly FileTextIcon = FileText;
  protected readonly CircleIcon = Circle;
  protected readonly PlusIcon = Plus;
  protected readonly ChevronsLeftIcon = ChevronsLeft;
  protected readonly ChevronLeftIcon = ChevronLeft;
  protected readonly ChevronRightIcon = ChevronRight;
  protected readonly ChevronsRightIcon = ChevronsRight;
  protected readonly ChartLineIcon = ChartLine;
  protected readonly FileClockIcon = FileClock;

  protected readonly activeTab = signal<ApplicationTab>('all');
  protected readonly showFilters = signal(false);
  protected readonly currentPage = signal(1);
  protected readonly pageSize = signal(10);
  protected readonly pageSizes = DEFAULT_PAGE_SIZE_OPTIONS;
  protected readonly statusFilter = signal<ApplicationStatusFilter>('all');
  protected readonly kindFilter = signal<ApplicationKindFilter>('all');
  protected readonly pendingPunchKindFilter = signal<ApplicationKindFilter>('all');
  protected readonly searchTerm = signal('');
  protected readonly applicationTypeFilter = signal('all');
  protected readonly fromDateFilter = signal('');
  protected readonly toDateFilter = signal('');
  protected readonly rawApplications = signal<ApplicationApi[]>([]);
  protected readonly contracts = signal<Contract[]>([]);
  protected readonly vendors = signal<Vendor[]>([]);
  protected readonly applicationTypeOptions = ['all', 'DAYS-AHEAD', 'CONTINGENCY', 'FCFS', 'ADVANCE'];

  protected readonly vendorMap = computed(() => new Map(this.vendors().map((vendor) => [vendor.id, vendor])));
  protected readonly contractMap = computed(() => new Map(this.contracts().map((contract) => [contract.id, contract])));
  protected readonly vendorId = computed(() => this.auth.user()?.vendor_id || '');
  protected readonly visibleContracts = computed(() => {
    const vendorId = this.vendorId();
    if (!vendorId) return this.contracts();
    return this.contracts().filter((contract) => (contract.party_details || []).some((party) => (party.vendor_id || '') === vendorId));
  });
  protected readonly visibleApplications = computed(() => {
    const vendorId = this.vendorId();
    if (!vendorId) return this.rawApplications();

    const visibleContractIds = new Set(this.visibleContracts().map((contract) => String(contract.id)));
    return this.rawApplications().filter((item) => {
      const contractId = String(item.contract_id || '');
      return (
        visibleContractIds.has(contractId) ||
        String(item.seller_vendor_id || '') === vendorId ||
        String(item.buyer_vendor_id || '') === vendorId
      );
    });
  });

  protected readonly records = computed<ApplicationRecord[]>(() =>
    this.visibleApplications().map((item) => {
      const seller = item.seller_vendor_id ? this.vendorMap().get(String(item.seller_vendor_id)) : null;
      const buyer = item.buyer_vendor_id ? this.vendorMap().get(String(item.buyer_vendor_id)) : null;
      const contract = this.contractMap().get(String(item.contract_id));
      const schedules = item.schedules || [];
      const sortedSchedules = [...schedules].sort((a, b) => (a.from_date || '').localeCompare(b.from_date || ''));
      const firstSchedule = sortedSchedules[0];
      const lastSchedule = sortedSchedules[sortedSchedules.length - 1];
      return {
        id: item.id,
        appNo: item.application_number,
        contractNo: contract?.contract_number || '-',
        appType: displayApplicationType(item.application_type),
        applicationDate: item.application_date || '',
        buyer: buyer?.vendor_name || '-',
        buyerVendorId: String(item.buyer_vendor_id || ''),
        buyerSldc: this.stateCode(buyer?.state),
        buyerRldc: buyer?.region || '-',
        seller: seller?.vendor_name || '-',
        sellerVendorId: String(item.seller_vendor_id || ''),
        sellerSldc: this.stateCode(seller?.state),
        sellerRldc: seller?.region || '-',
        sellerRegistrationCode: seller?.noar_id || seller?.exchange_portfolio_id || '-',
        quantum: this.applicationScheduleQuantumMwh(item, contract),
        corridor: this.routeLabels(item.route),
        validFrom: this.ddmmyyyy(firstSchedule?.from_date || item.application_date),
        validTo: this.ddmmyyyy(lastSchedule?.to_date || item.application_date),
        validFromRaw: firstSchedule?.from_date || item.application_date || '',
        validToRaw: lastSchedule?.to_date || item.application_date || '',
        hoursFrom: firstSchedule?.from_time || '-',
        hoursTo: lastSchedule?.to_time || '-',
        status: this.normalizeStatus(item.status),
      };
    }),
  );

  protected readonly nextMonthApplicationIds = computed(() =>
    new Set(
      this.visibleApplications()
        .filter((item) => (item.status || '').toLowerCase() === 'punched' && this.hasNextMonthSchedule(item.schedules || []))
        .map((item) => item.id),
    ),
  );

  protected readonly monthlyAdvanceRecords = computed(() => this.records().filter((item) => this.nextMonthApplicationIds().has(item.id)));
  protected readonly monthlyAdvanceCount = computed(() => this.monthlyAdvanceRecords().length);
  protected readonly monthlyAdvanceQuantum = computed(() => this.monthlyAdvanceRecords().reduce((sum, row) => sum + row.quantum, 0));

  protected readonly pendingPunchRows = computed<PendingPunchRow[]>(() => {
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const nextMonthStart = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    const appsByContract = new Map<string, ApplicationApi[]>();
    // Build suggestions from the same visible application universe used by the page
    // so deleted/irrelevant rows do not suppress a contract's OA punch suggestion.
    this.visibleApplications().forEach((app) => {
      const key = String(app.contract_id);
      const items = appsByContract.get(key) || [];
      items.push(app);
      appsByContract.set(key, items);
    });

    return this.visibleContracts()
      .filter((contract) => {
        const status = (contract.status || '').toLowerCase().trim();
        if (status !== 'approved' && status !== 'active') return false;
        const from = new Date(`${contract.period_from}T00:00:00`);
        const to = new Date(`${contract.period_to}T23:59:59`);
        return from < nextMonthStart && to >= monthStart;
      })
      .map((contract) => {
        const apps = appsByContract.get(contract.id) || [];
        const currentMonthApps = apps.filter((app) => this.applicationOverlapsRange(app, monthStart, nextMonthStart));
        const applicationNumbers = Array.from(
          new Set(
            currentMonthApps
              .map((app) => (app.application_number || '').trim())
              .filter((value) => !!value),
          ),
        );
        const punchedApps = currentMonthApps.filter((app) => (app.status || '').toLowerCase() === 'punched');
        const alreadyScheduled = punchedApps.reduce(
          (sum, app) => sum + this.punchedScheduleQuantumMwhInRange(app, monthStart, nextMonthStart, contract),
          0,
        );
        const quantum = this.contractQuantumMwhInRange(contract, monthStart, nextMonthStart);
        const buyers = this.contractParties(contract, 'buyer');
        const sellers = this.contractParties(contract, 'seller');
        return {
          contractId: contract.id,
          contractNo: contract.contract_number,
          applicationCount: applicationNumbers.length,
          applicationNumbers,
          buyerParties: buyers,
          sellerParties: sellers,
          type: contract.contract_type || '-',
          period: `${this.ddmmyyyy(contract.period_from)} to ${this.ddmmyyyy(contract.period_to)}`,
          quantum,
          alreadyScheduled,
          remainingToPunch: Math.max(quantum - alreadyScheduled, 0),
        };
      })
      .filter((row) => row.remainingToPunch > 0);
  });

  protected readonly displayPendingPunchRows = computed(() => {
    const filter = this.pendingPunchKindFilter();
    const search = this.searchTerm().trim().toLowerCase();
    let rows = this.pendingPunchRows();
    if (filter !== 'all') {
      rows = rows.filter((row) => this.contractKind(row.type) === filter);
    }
    if (!search) return rows;
    return rows.filter((row) =>
      [
        row.contractNo,
        row.type,
        row.period,
        ...row.applicationNumbers,
        ...row.buyerParties.map((party) => party.name),
        ...row.sellerParties.map((party) => party.name),
      ]
        .join(' ')
        .toLowerCase()
        .includes(search),
    );
  });

  protected readonly pendingPunchCount = computed(() => this.pendingPunchRows().length);
  protected readonly pendingPunchTotalQuantum = computed(() => this.pendingPunchRows().reduce((sum, row) => sum + row.remainingToPunch, 0));
  protected readonly displayPendingPunchCount = computed(() => this.displayPendingPunchRows().length);
  protected readonly displayPendingPunchTotalQuantum = computed(() => this.displayPendingPunchRows().reduce((sum, row) => sum + row.remainingToPunch, 0));
  protected readonly totalRows = computed(() => (this.activeTab() === 'pendingPunch' ? this.displayPendingPunchRows().length : this.displayRecords().length));
  protected readonly totalPages = computed(() => Math.max(1, Math.ceil(this.totalRows() / this.pageSize())));
  protected readonly currentPageSafe = computed(() => Math.min(this.currentPage(), this.totalPages()));
  protected readonly pageStartIndex = computed(() => (this.currentPageSafe() - 1) * this.pageSize());
  protected readonly pageStart = computed(() => (this.totalRows() ? this.pageStartIndex() + 1 : 0));
  protected readonly pageEnd = computed(() => Math.min(this.pageStartIndex() + this.pageSize(), this.totalRows()));
  protected readonly pagedDisplayPendingPunchRows = computed(() => {
    const start = this.pageStartIndex();
    return this.displayPendingPunchRows().slice(start, start + this.pageSize());
  });

  protected readonly displayRecords = computed(() => {
    let source = this.activeTab() === 'monthlyAdvance' ? this.monthlyAdvanceRecords() : this.records();
    const statusFilter = this.statusFilter();
    const kindFilter = this.kindFilter();
    const typeFilter = this.applicationTypeFilter();
    const search = this.searchTerm().trim().toLowerCase();
    const fromDate = this.fromDateFilter();
    const toDate = this.toDateFilter();
    const pendingApprovalMap = new Map(this.pendingApprovalRecords().map((item) => [item.id, item]));
    if (statusFilter !== 'all') {
      source =
        statusFilter === 'pending'
          ? source.map((item) => pendingApprovalMap.get(item.id) ?? item).filter((item) => item.status === 'pending')
          : statusFilter === 'approved'
            ? source.filter((item) => item.status === 'approved' || item.status === 'punched')
            : source.filter((item) => item.status === statusFilter);
    }
    if (kindFilter !== 'all') {
      source = source.filter((item) => this.applicationKind(item.appType) === kindFilter);
    }
    if (typeFilter !== 'all') {
      source = source.filter((item) => normalizeApplicationType(item.appType) === typeFilter);
    }
    if (fromDate) {
      source = source.filter((item) => item.applicationDate >= fromDate);
    }
    if (toDate) {
      source = source.filter((item) => item.applicationDate <= toDate);
    }
    if (search) {
      source = source.filter((item) =>
        [
          item.appNo,
          item.contractNo,
          item.appType,
          item.seller,
          item.buyer,
          item.sellerSldc,
          item.buyerSldc,
          ...item.corridor,
        ]
          .join(' ')
          .toLowerCase()
          .includes(search),
      );
    }
    return source;
  });
  protected readonly displayQuantum = computed(() => this.displayRecords().reduce((sum, row) => sum + row.quantum, 0));
  protected readonly pagedDisplayRecords = computed(() => {
    const start = this.pageStartIndex();
    return this.displayRecords().slice(start, start + this.pageSize());
  });
  protected readonly pendingApprovalRecords = computed(() =>
    this.visibleApplications()
      .filter((item) => (item.status || '').toLowerCase().trim() === 'punched' && !(Number(item.approved_quantum) > 0))
      .map((item) => {
        const seller = item.seller_vendor_id ? this.vendorMap().get(String(item.seller_vendor_id)) : null;
        const buyer = item.buyer_vendor_id ? this.vendorMap().get(String(item.buyer_vendor_id)) : null;
        const contract = this.contractMap().get(String(item.contract_id));
        const schedules = item.schedules || [];
        const sortedSchedules = [...schedules].sort((a, b) => (a.from_date || '').localeCompare(b.from_date || ''));
        const firstSchedule = sortedSchedules[0];
        const lastSchedule = sortedSchedules[sortedSchedules.length - 1];
        return {
          id: item.id,
          appNo: item.application_number,
          contractNo: contract?.contract_number || '-',
          appType: displayApplicationType(item.application_type),
          applicationDate: item.application_date || '',
          buyer: buyer?.vendor_name || '-',
          buyerVendorId: String(item.buyer_vendor_id || ''),
          buyerSldc: this.stateCode(buyer?.state),
          buyerRldc: buyer?.region || '-',
          seller: seller?.vendor_name || '-',
          sellerVendorId: String(item.seller_vendor_id || ''),
          sellerSldc: this.stateCode(seller?.state),
          sellerRldc: seller?.region || '-',
          sellerRegistrationCode: seller?.noar_id || seller?.exchange_portfolio_id || '-',
          quantum: this.applicationScheduleQuantumMwh(item, contract),
          corridor: this.routeLabels(item.route),
          validFrom: this.ddmmyyyy(firstSchedule?.from_date || item.application_date),
          validTo: this.ddmmyyyy(lastSchedule?.to_date || item.application_date),
          validFromRaw: firstSchedule?.from_date || item.application_date || '',
          validToRaw: lastSchedule?.to_date || item.application_date || '',
          hoursFrom: firstSchedule?.from_time || '-',
          hoursTo: lastSchedule?.to_time || '-',
          status: 'pending' as const,
        };
      }),
  );

  protected readonly pendingBreakdown = computed(() => {
    const pending = this.pendingApprovalRecords();
    const total = pending.reduce((sum, item) => sum + item.quantum, 0);
    const bucket = (label: string, aliases: string[]) => {
      const rows = pending.filter((item) => aliases.includes(item.appType.toUpperCase()));
      const quantum = rows.reduce((sum, item) => sum + item.quantum, 0);
      return {
        type: label,
        quantum,
        count: rows.length,
        progress: total > 0 ? Math.max((quantum / total) * 100, rows.length ? 8 : 0) : 0,
      };
    };
    return [
      bucket('FCFS', ['FCFS']),
      bucket('DAY-AHEAD', ['DAYS-AHEAD', 'DAY-AHEAD']),
      bucket('CONTINGENCY', ['CONTINGENCY']),
      bucket('ADVANCE', ['ADVANCE']),
    ];
  });

  protected readonly totalApplications = computed(() => this.records().length);
  protected readonly totalQuantum = computed(() => this.records().reduce((sum, record) => sum + record.quantum, 0));
  protected readonly approvedCount = computed(() => this.records().filter((record) => record.status === 'approved' || record.status === 'punched').length);
  protected readonly pendingCount = computed(() => this.pendingApprovalRecords().length);
  protected readonly rejectedCount = computed(() => this.records().filter((record) => record.status === 'rejected').length);
  protected readonly approvedQuantum = computed(() =>
    this.records()
      .filter((record) => record.status === 'approved' || record.status === 'punched')
      .reduce((sum, record) => sum + record.quantum, 0),
  );
  protected readonly pendingQuantum = computed(() =>
    this.pendingApprovalRecords().reduce((sum, record) => sum + record.quantum, 0),
  );
  protected readonly rejectedQuantum = computed(() =>
    this.records()
      .filter((record) => record.status === 'rejected')
      .reduce((sum, record) => sum + record.quantum, 0),
  );
  protected readonly activeContractRows = computed(() =>
    this.visibleContracts().filter((contract) => {
      const status = (contract.status || '').toLowerCase().trim();
      return status === 'approved' || status === 'active';
    }),
  );
  protected readonly activeContractCount = computed(() => this.activeContractRows().length);
  protected readonly activeContractTypeStats = computed(() => {
    const stats = (kind: 'banking' | 'salePurchase' | 'hybrid') => {
      const filtered = this.activeContractRows().filter((contract) => this.contractCategoryKind(contract) === kind);
      return {
        count: filtered.length,
        quantum: filtered.length * MW_PER_CONTRACT,
        progress: 0,
      };
    };
    return {
      banking: stats('banking'),
      salePurchase: stats('salePurchase'),
      hybrid: stats('hybrid'),
    };
  });
  protected readonly autoSuggestedStats = computed(() => {
    const rows = this.pendingPunchRows();
    const byKind = (kind: 'banking' | 'salePurchase' | 'hybrid') =>
      rows.filter((row) => this.contractKind(row.type) === kind);
    const banking = byKind('banking');
    const salePurchase = byKind('salePurchase');
    const hybrid = byKind('hybrid');
    return {
      totalQuantum: rows.reduce((sum, row) => sum + row.remainingToPunch, 0),
      bankingCount: banking.length,
      salePurchaseCount: salePurchase.length,
      hybridCount: hybrid.length,
      bankingQuantum: banking.reduce((sum, row) => sum + row.remainingToPunch, 0),
      salePurchaseQuantum: salePurchase.reduce((sum, row) => sum + row.remainingToPunch, 0),
      hybridQuantum: hybrid.reduce((sum, row) => sum + row.remainingToPunch, 0),
      totalCount: rows.length,
    };
  });
  protected readonly summaryTiles = computed(() => [
    {
      label: 'Total Applications',
      count: this.totalApplications(),
      icon: this.ClipboardListIcon,
      categories: [
        { label: 'Approved', key: 'success', count: this.approvedCount(), mw: this.approvedQuantum() },
        { label: 'Pending', key: 'warning', count: this.pendingCount(), mw: this.pendingQuantum() },
        { label: 'Rejected', key: 'danger', count: this.rejectedCount(), mw: this.rejectedQuantum() },
      ],
    },
    {
      label: 'Active Contracts',
      count: this.activeContractCount(),
      icon: this.ChartLineIcon,
      categories: [
        { label: 'Sale Purchase', key: 'sale', count: this.activeContractTypeStats().salePurchase.count, mw: this.activeContractTypeStats().salePurchase.quantum },
        { label: 'Banking', key: 'bank', count: this.activeContractTypeStats().banking.count, mw: this.activeContractTypeStats().banking.quantum },
        { label: 'Hybrid', key: 'hybrid', count: this.activeContractTypeStats().hybrid.count, mw: this.activeContractTypeStats().hybrid.quantum },
      ],
    },
    {
      label: 'Auto Suggested',
      count: this.autoSuggestedStats().totalCount,
      icon: this.SparklesIcon,
      categories: [
        { label: 'Sale Purchase', key: 'sale', count: this.autoSuggestedStats().salePurchaseCount, mw: this.autoSuggestedStats().salePurchaseQuantum },
        { label: 'Banking', key: 'bank', count: this.autoSuggestedStats().bankingCount, mw: this.autoSuggestedStats().bankingQuantum },
        { label: 'Hybrid', key: 'hybrid', count: this.autoSuggestedStats().hybridCount, mw: this.autoSuggestedStats().hybridQuantum },
      ],
    },
    {
      label: 'Pending for Approval',
      count: this.pendingCount(),
      icon: this.FileClockIcon,
      categories: this.pendingBreakdown().map((item, index) => ({
        label: item.type,
        key: index === 0 ? 'info' : index === 1 ? 'warning' : index === 2 ? 'danger' : 'success',
        count: item.count,
        mw: item.quantum,
      })),
    },
  ]);

  ngOnInit(): void {
    this.api.get<ApplicationApi[]>('/api/applications').subscribe({
      next: (data) => this.rawApplications.set(data || []),
      error: () => this.rawApplications.set([]),
    });
    this.contractsService.list().subscribe({
      next: (data) => this.contracts.set(data || []),
      error: () => this.contracts.set([]),
    });
    this.vendorsService.list().subscribe({
      next: (data) => this.vendors.set(data || []),
      error: () => this.vendors.set([]),
    });
  }

  protected setActiveTab(tab: ApplicationTab): void {
    this.activeTab.set(tab);
    this.resetPagination();
    if (tab !== 'all') {
      this.statusFilter.set('all');
      this.kindFilter.set('all');
    }
    if (tab !== 'pendingPunch') {
      this.pendingPunchKindFilter.set('all');
    }
  }

  protected toggleFilters(): void {
    this.showFilters.update((value) => !value);
  }

  protected updateSearchTerm(value: string): void {
    this.searchTerm.set(value);
    this.resetPagination();
  }

  protected updateApplicationTypeFilter(value: string): void {
    const normalized = normalizeApplicationType(value);
    this.applicationTypeFilter.set(this.applicationTypeOptions.includes(normalized) ? normalized : 'all');
    this.resetPagination();
  }

  protected updateStatusFilter(value: string): void {
    this.statusFilter.set(['all', 'approved', 'pending', 'rejected'].includes(value) ? (value as ApplicationStatusFilter) : 'all');
    this.resetPagination();
  }

  protected updatePendingPunchKindFilter(value: string): void {
    this.pendingPunchKindFilter.set(['all', 'salePurchase', 'banking', 'hybrid'].includes(value) ? (value as ApplicationKindFilter) : 'all');
    this.resetPagination();
  }

  protected updateFromDateFilter(value: string): void {
    this.fromDateFilter.set(value);
    this.resetPagination();
  }

  protected updateToDateFilter(value: string): void {
    this.toDateFilter.set(value);
    this.resetPagination();
  }

  protected clearAllFilters(): void {
    this.searchTerm.set('');
    this.applicationTypeFilter.set('all');
    this.fromDateFilter.set('');
    this.toDateFilter.set('');
    this.statusFilter.set('all');
    this.kindFilter.set('all');
    this.pendingPunchKindFilter.set('all');
    this.resetPagination();
  }

  protected hasActiveFilters(): boolean {
    return (
      !!this.searchTerm().trim() ||
      this.applicationTypeFilter() !== 'all' ||
      !!this.fromDateFilter() ||
      !!this.toDateFilter() ||
      this.statusFilter() !== 'all' ||
      this.kindFilter() !== 'all' ||
      this.pendingPunchKindFilter() !== 'all'
    );
  }

  protected applyStatusFilter(filter: ApplicationStatusFilter): void {
    this.activeTab.set('all');
    this.showFilters.set(true);
    this.statusFilter.set(filter);
    this.kindFilter.set('all');
    this.resetPagination();
  }

  protected isStatusFilterActive(filter: ApplicationStatusFilter): boolean {
    return this.activeTab() === 'all' && this.statusFilter() === filter;
  }

  protected applyKindFilter(filter: ApplicationKindFilter): void {
    this.activeTab.set('pendingPunch');
    this.showFilters.set(true);
    this.pendingPunchKindFilter.set(filter);
    this.kindFilter.set('all');
    this.statusFilter.set('all');
    this.resetPagination();
  }

  protected goToPreviousPage(): void {
    if (this.currentPageSafe() <= 1) return;
    this.currentPage.set(this.currentPageSafe() - 1);
  }

  protected goToNextPage(): void {
    if (this.currentPageSafe() >= this.totalPages()) return;
    this.currentPage.set(this.currentPageSafe() + 1);
  }

  protected onPageSizeChange(value: string): void {
    const parsed = Number(value);
    const nextSize = this.pageSizes.find((size) => size === parsed) ?? this.pageSizes[0];
    this.pageSize.set(nextSize);
    this.resetPagination();
  }

  protected canGoToPreviousPage(): boolean {
    return this.currentPageSafe() > 1;
  }

  protected canGoToNextPage(): boolean {
    return this.currentPageSafe() < this.totalPages();
  }

  private resetPagination(): void {
    this.currentPage.set(1);
  }

  protected isKindFilterActive(filter: ApplicationKindFilter): boolean {
    return this.activeTab() === 'pendingPunch' && this.pendingPunchKindFilter() === filter;
  }

  protected openActiveContracts(filter: ApplicationKindFilter): void {
    void this.router.navigate(["/contracts"], {
      queryParams: {
        contractStatus: "active",
        category: filter,
      },
    });
  }

  protected openVendorDetails(vendorId?: string | null): void {
    const id = (vendorId || "").trim();
    if (!id) return;
    const url = this.router.serializeUrl(this.router.createUrlTree(["/vendors", id]));
    window.open(url, "_blank");
  }

  private normalizeStatus(status: string): ApplicationStatus {
    const value = (status || '').toLowerCase().trim();
    if (value === 'approved') return 'approved';
    if (value === 'rejected') return 'rejected';
    if (value === 'punched') return 'punched';
    return 'pending';
  }

  private routeLabels(routeValue: string | null | undefined): string[] {
    if (!routeValue) return [];
    return routeValue
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean)
      .map((item) => item.replaceAll('_', '-'));
  }

  private stateCode(state: string | null | undefined): string {
    if (!state) return '-';
    const words = state.trim().split(/\s+/);
    if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
    return words.map((word) => word[0]).join('').toUpperCase();
  }

  private contractParties(contract: Contract, kind: 'buyer' | 'seller'): Array<{ vendorId: string; name: string }> {
    const parties = contract.party_details || [];
    const hasTypedParties = parties.some((party) => !!party.party_type);
    const partyRows = parties
      .filter((party, index) => {
        const partyType = (party.party_type || '').toLowerCase();
        if (hasTypedParties) {
          return kind === 'buyer'
            ? partyType.includes('buyer') || partyType.includes('buy')
            : partyType.includes('seller') || partyType.includes('sell');
        }
        return kind === 'buyer' ? index % 2 === 0 : index % 2 === 1;
      })
      .map((party) => {
        const vendorId = String(party.vendor_id || '');
        return {
          vendorId,
          name: this.vendorMap().get(vendorId)?.vendor_name || '-',
        };
      });
    return Array.from(new Map(partyRows.map((party) => [party.vendorId + '::' + party.name, party])).values());
  }

  private ddmmyyyy(dateText: string | undefined): string {
    if (!dateText) return '-';
    const [yyyy, mm, dd] = dateText.split('-');
    if (!yyyy || !mm || !dd) return dateText;
    return `${dd}-${mm}-${yyyy}`;
  }

  private hasNextMonthSchedule(schedules: ApplicationScheduleApi[]): boolean {
    if (!schedules.length) return false;
    const now = new Date();
    const nextMonthStart = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    const monthAfterNextStart = new Date(now.getFullYear(), now.getMonth() + 2, 1);
    return schedules.some((schedule) => {
      const from = new Date(`${schedule.from_date}T00:00:00`);
      const to = new Date(`${schedule.to_date}T23:59:59`);
      return from < monthAfterNextStart && to >= nextMonthStart;
    });
  }

  private contractKind(value: string): 'banking' | 'salePurchase' | 'hybrid' {
    const text = (value || '').toLowerCase();
    if (text.includes('hybrid')) return 'hybrid';
    if (text.includes('bank')) return 'banking';
    return 'salePurchase';
  }

  private contractCategoryKind(contract: Contract): 'banking' | 'salePurchase' | 'hybrid' {
    const savedCategory = (contract.contract_category || '').trim().toUpperCase();
    if (savedCategory === 'BANKING') return 'banking';
    if (savedCategory === 'HYBRID') return 'hybrid';
    if (savedCategory === 'SALE-PURCHASE') return 'salePurchase';
    return this.contractKind(contract.contract_type || '');
  }

  private applicationKind(value: string): 'banking' | 'salePurchase' | 'hybrid' {
    const text = (value || '').toLowerCase();
    if (text.includes('hybrid')) return 'hybrid';
    if (text.includes('bank')) return 'banking';
    return 'salePurchase';
  }

  private contractQuantumMwh(contract: Contract): number {
    const start = this.parseDateOnly(contract.period_from);
    const end = this.parseDateOnly(contract.period_to);
    if (!start || !end) return 0;

    const dayDifference = this.inclusiveDayCount(start, end);
    if (!dayDifference) return 0;

    const baseQuantum = (contract.party_details || []).reduce((sum, party) => sum + (Number(party.quantum) || 0), 0);
    if (baseQuantum <= 0) return 0;

    return dayDifference * 24 * baseQuantum * 250;
  }

  private contractQuantumMwhInRange(contract: Contract, rangeStart: Date, rangeEndExclusive: Date): number {
    const start = this.parseDateOnly(contract.period_from);
    const end = this.parseDateOnly(contract.period_to);
    if (!start || !end) return 0;

    const overlap = this.overlapDateRange(start, end, rangeStart, new Date(rangeEndExclusive.getTime() - 86400000));
    if (!overlap) return 0;

    const dayDifference = this.inclusiveDayCount(overlap.start, overlap.end);
    if (!dayDifference) return 0;

    const baseQuantum = (contract.party_details || []).reduce((sum, party) => sum + (Number(party.quantum) || 0), 0);
    if (baseQuantum <= 0) return 0;

    return dayDifference * 24 * baseQuantum * 250;
  }

  private approvedScheduleQuantumMwh(application: ApplicationApi): number {
    const approvedQuantum = Number(application.approved_quantum) || 0;
    if (approvedQuantum <= 0) return 0;

    return (application.schedules || []).reduce((sum, schedule) => {
      const start = this.parseDateOnly(schedule.from_date);
      const end = this.parseDateOnly(schedule.to_date || schedule.from_date);
      if (!start || !end) return sum;

      const dayDifference = this.inclusiveDayCount(start, end);
      if (!dayDifference) return sum;

      const hourDifference = this.timeDifferenceHours(schedule.from_time, schedule.to_time);
      if (!hourDifference) return sum;

      return sum + dayDifference * hourDifference * approvedQuantum * 250;
    }, 0);
  }

  private approvedScheduleQuantumMwhInRange(application: ApplicationApi, rangeStart: Date, rangeEndExclusive: Date): number {
    const approvedQuantum = Number(application.approved_quantum) || 0;
    if (approvedQuantum <= 0) return 0;

    return (application.schedules || []).reduce((sum, schedule) => {
      const start = this.parseDateOnly(schedule.from_date);
      const end = this.parseDateOnly(schedule.to_date || schedule.from_date);
      if (!start || !end) return sum;

      const overlap = this.overlapDateRange(start, end, rangeStart, new Date(rangeEndExclusive.getTime() - 86400000));
      if (!overlap) return sum;

      const dayDifference = this.inclusiveDayCount(overlap.start, overlap.end);
      if (!dayDifference) return sum;

      const hourDifference = this.timeDifferenceHours(schedule.from_time, schedule.to_time);
      if (!hourDifference) return sum;

      return sum + dayDifference * hourDifference * approvedQuantum * 250;
    }, 0);
  }

  private punchedScheduleQuantumMwhInRange(
    application: ApplicationApi,
    rangeStart: Date,
    rangeEndExclusive: Date,
    contract?: Contract | null,
  ): number {
    const status = (application.status || '').toLowerCase().trim();
    if (status !== 'punched') {
      const approvedTotal = this.approvedScheduleQuantumMwhInRange(application, rangeStart, rangeEndExclusive);
      if (approvedTotal > 0) return approvedTotal;
    }

    const scheduledTotal = (application.schedules || []).reduce((sum, schedule) => {
      const quantum = Number(schedule.quantum) || 0;
      if (quantum <= 0) return sum;

      const start = this.parseDateOnly(schedule.from_date);
      const end = this.parseDateOnly(schedule.to_date || schedule.from_date);
      if (!start || !end) return sum;

      const overlap = this.overlapDateRange(start, end, rangeStart, new Date(rangeEndExclusive.getTime() - 86400000));
      if (!overlap) return sum;

      const dayDifference = this.inclusiveDayCount(overlap.start, overlap.end);
      if (!dayDifference) return sum;

      const hourDifference = this.timeDifferenceHours(schedule.from_time, schedule.to_time);
      if (!hourDifference) return sum;

      return sum + dayDifference * hourDifference * quantum * 250;
    }, 0);

    const contractCoveredTotal = this.fullyCoveredContractQuantumMwh(application, contract, rangeStart, rangeEndExclusive);
    if (contractCoveredTotal !== null) {
      return Math.max(scheduledTotal, contractCoveredTotal);
    }

    return scheduledTotal > 0 ? scheduledTotal : Number(application.total_quantum) || 0;
  }

  private applicationScheduleQuantumMwh(application: ApplicationApi, contract?: Contract | null): number {
    const total = (application.schedules || []).reduce((sum, schedule) => {
      const quantum = Number(schedule.quantum) || 0;
      if (quantum <= 0) return sum;

      const start = this.parseDateOnly(schedule.from_date);
      const end = this.parseDateOnly(schedule.to_date || schedule.from_date);
      if (!start || !end) return sum;

      const dayDifference = this.inclusiveDayCount(start, end);
      if (!dayDifference) return sum;

      const hourDifference = this.timeDifferenceHours(schedule.from_time, schedule.to_time);
      if (!hourDifference) return sum;

      return sum + dayDifference * hourDifference * quantum * 250;
    }, 0);

    const contractCoveredTotal = this.fullyCoveredContractQuantumMwh(application, contract);
    if (contractCoveredTotal !== null) {
      return Math.max(total, contractCoveredTotal);
    }

    return total > 0 ? total : Number(application.total_quantum) || 0;
  }

  private fullyCoveredContractQuantumMwh(
    application: ApplicationApi,
    contract?: Contract | null,
    rangeStart?: Date,
    rangeEndExclusive?: Date,
  ): number | null {
    const status = (application.status || '').toLowerCase().trim();
    if (status !== 'punched' && status !== 'approved') return null;
    if (!contract) return null;

    const schedules = application.schedules || [];
    if (!schedules.length) return null;

    const contractStart = this.parseDateOnly(contract.period_from);
    const contractEnd = this.parseDateOnly(contract.period_to);
    if (!contractStart || !contractEnd) return null;

    const firstFrom = schedules
      .map((schedule) => this.parseDateOnly(schedule.from_date))
      .filter((value): value is Date => value !== null)
      .sort((a, b) => a.getTime() - b.getTime())[0];
    const lastTo = schedules
      .map((schedule) => this.parseDateOnly(schedule.to_date || schedule.from_date))
      .filter((value): value is Date => value !== null)
      .sort((a, b) => b.getTime() - a.getTime())[0];

    if (!firstFrom || !lastTo) return null;
    if (firstFrom.getTime() > contractStart.getTime() || lastTo.getTime() < contractEnd.getTime()) return null;

    const boundedStart = rangeStart ?? contractStart;
    const boundedEnd = rangeEndExclusive ? new Date(rangeEndExclusive.getTime() - 86400000) : contractEnd;
    const overlap = this.overlapDateRange(contractStart, contractEnd, boundedStart, boundedEnd);
    if (!overlap) return null;

    const quantum =
      status === 'punched'
        ? Number(application.total_quantum) || Number(application.approved_quantum) || 0
        : Number(application.approved_quantum) || Number(application.total_quantum) || 0;
    if (quantum <= 0) return null;

    const dayDifference = this.inclusiveDayCount(overlap.start, overlap.end);
    if (!dayDifference) return null;

    return dayDifference * 24 * quantum * 250;
  }

  private parseDateOnly(value: string | null | undefined): Date | null {
    if (!value) return null;
    const [year, month, day] = value.trim().split('-').map((part) => Number(part));
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day);
  }

  private applicationOverlapsRange(application: ApplicationApi, rangeStart: Date, rangeEndExclusive: Date): boolean {
    const rangeEndInclusive = new Date(rangeEndExclusive.getTime() - 86400000);
    const schedules = application.schedules || [];
    if (schedules.length) {
      return schedules.some((schedule) => {
        const start = this.parseDateOnly(schedule.from_date);
        const end = this.parseDateOnly(schedule.to_date || schedule.from_date);
        if (!start || !end) return false;
        return this.overlapDateRange(start, end, rangeStart, rangeEndInclusive) !== null;
      });
    }

    const applicationDate = this.parseDateOnly(application.application_date);
    if (!applicationDate) return false;
    return this.overlapDateRange(applicationDate, applicationDate, rangeStart, rangeEndInclusive) !== null;
  }

  private overlapDateRange(
    startA: Date,
    endA: Date,
    startB: Date,
    endB: Date,
  ): { start: Date; end: Date } | null {
    const start = new Date(Math.max(startA.getTime(), startB.getTime()));
    const end = new Date(Math.min(endA.getTime(), endB.getTime()));
    if (end.getTime() < start.getTime()) return null;
    return { start, end };
  }

  private inclusiveDayCount(start: Date, end: Date): number {
    const diffMs = end.getTime() - start.getTime();
    if (diffMs < 0) return 0;
    return Math.floor(diffMs / 86400000) + 1;
  }

  private timeDifferenceHours(fromTime: string | null | undefined, toTime: string | null | undefined): number {
    const fromMinutes = this.parseTimeToMinutes(fromTime);
    const toMinutes = this.parseTimeToMinutes(toTime, true);
    if (fromMinutes === null || toMinutes === null || toMinutes <= fromMinutes) return 0;
    return (toMinutes - fromMinutes) / 60;
  }

  private parseTimeToMinutes(value: string | null | undefined, allow24Hour = false): number | null {
    const raw = (value || '').trim();
    if (!raw) return allow24Hour ? 1440 : 0;
    const [hourRaw, minuteRaw] = raw.split(':');
    const hour = Number(hourRaw);
    const minute = Number(minuteRaw);
    if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null;
    if (allow24Hour && hour === 24 && minute === 0) return 1440;
    if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;
    return hour * 60 + minute;
  }

  protected downloadFormatD(record: ApplicationRecord): void {
    if (record.status !== 'punched' && record.status !== 'approved') {
      this.toast.show('Format-D is available only for punched/approved applications', '', 'danger');
      return;
    }
    const html = this.buildFormatDHtml(record);
    const printWindow = window.open('', '_blank', 'width=1024,height=768');
    if (!printWindow) {
      this.toast.show('Popup blocked. Please allow popups to download Format-D as PDF.', '', 'danger');
      return;
    }
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
  }

  protected exportApplications(): void {
    if (this.activeTab() === 'pendingPunch') {
      const headers = [
        'S. No.',
        'Contract No.',
        'Seller',
        'Buyer',
        'Type',
        'Application Count',
        'Period',
        'Quantum (MWh)',
        'Already Scheduled (MWh)',
        'Remaining to Punch (MWh)',
        'Route Details',
      ];
      const rows = this.displayPendingPunchRows().map((row, index) => [
        index + 1,
        row.contractNo,
        row.sellerParties.map((party) => party.name).join(' | ') || '-',
        row.buyerParties.map((party) => party.name).join(' | ') || '-',
        row.type,
        row.applicationCount,
        row.period,
        row.quantum,
        row.alreadyScheduled,
        row.remainingToPunch,
        'Injection: - | Drawal: - | SLDC: - | RLDC: -',
      ]);
      this.downloadCsv(headers, rows, 'applications_pending_punch');
      return;
    }

    const headers = [
      'S. No.',
      'Application No.',
      'Contract No.',
      'Application Type',
      'Seller',
      'Seller SLDC',
      'Seller RLDC',
      'Buyer',
      'Buyer SLDC',
      'Buyer RLDC',
      'Quantum (MWh)',
      'Corridor',
      'Valid From',
      'Valid To',
      'Status',
    ];
    const rows = this.displayRecords().map((record, index) => [
      index + 1,
      record.appNo,
      record.contractNo,
      record.appType,
      record.seller,
      record.sellerSldc,
      record.sellerRldc,
      record.buyer,
      record.buyerSldc,
      record.buyerRldc,
      record.quantum,
      record.corridor.join(' | '),
      record.validFrom,
      record.validTo,
      record.status,
    ]);
    const fileKey = this.activeTab() === 'monthlyAdvance' ? 'applications_monthly_advance' : 'applications_all';
    this.downloadCsv(headers, rows, fileKey);
  }

  private downloadCsv(headers: string[], rows: Array<Array<string | number>>, fileKey: string): void {
    const csv = [headers.join(','), ...rows.map((row) => row.map((value) => `"${String(value ?? '').replace(/"/g, '""')}"`).join(','))].join('\n');
    const blob = new Blob(['\uFEFF', csv], { type: 'text/csv;charset=utf-8;' });
    const anchor = document.createElement('a');
    anchor.href = URL.createObjectURL(blob);
    anchor.download = `${fileKey}_${new Date().toISOString().slice(0, 10)}.csv`;
    anchor.click();
    URL.revokeObjectURL(anchor.href);
  }

  private buildFormatDHtml(record: ApplicationRecord): string {
    const todayDate = this.formatDateForPdf(new Date().toISOString().slice(0, 10));
    const corridor = record.corridor.length ? record.corridor.join(', ') : '-';
    const applicantName = record.seller !== '-' ? record.seller : record.buyer;
    const registrationCode = record.sellerRegistrationCode || '-';
    const injectionRegion = record.sellerRldc || record.sellerSldc || '-';
    const escape = (value: string | number) => this.escapeHtml(String(value ?? '-'));

    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>Format D - ${escape(record.appNo)}</title>
  <style>
    body { margin: 0; background: #f4f6fb; font-family: Arial, sans-serif; color: #111827; }
    .pdf-page { padding: 24px; }
    .format-d { width: 100%; max-width: 980px; margin: 0 auto; background: #fff; padding: 24px; box-sizing: border-box; }
    .header { text-align: center; margin-bottom: 16px; }
    .format-label { font-weight: 700; margin-bottom: 8px; }
    .title { font-weight: 700; font-size: 18px; line-height: 1.35; }
    .pdftable { width: 100%; border-collapse: collapse; font-size: 13px; }
    .pdftable td, .pdftable th { border: 1px solid #222; padding: 8px; vertical-align: middle; }
    .center { text-align: center; }
    .declaration { margin-top: 18px; font-size: 13px; line-height: 1.5; }
    .declaration ol { margin: 8px 0 0 18px; padding: 0; }
    .signature-row { display: flex; justify-content: space-between; gap: 16px; margin-top: 28px; font-size: 13px; }
    .signature-block { text-align: right; }
    @page { size: A4; margin: 12mm; }
    @media print {
      body { background: #fff; }
      .pdf-page { padding: 0; }
      .format-d { max-width: none; margin: 0; padding: 0; }
    }
  </style>
</head>
<body>
  <div class="pdf-page">
    <div id="formatD" class="format-d">
      <div class="header">
        <div class="format-label">Format-D</div>
        <div class="title">T-GNA/T-GNARE (Bilateral Transaction) – Application for Grant of T-GNA/T-GNARE</div>
      </div>

      <table class="pdftable">
        <tr>
          <td>1</td>
          <td>Application No.</td>
          <td colspan="2">${escape(record.appNo || '-')}</td>
          <td>Date</td>
          <td colspan="2">${escape(this.formatDateForPdf(record.applicationDate))}</td>
        </tr>
        <tr>
          <td>2</td>
          <td>Applicant Name</td>
          <td colspan="2">${escape(applicantName)}</td>
          <td>Registration Code</td>
          <td colspan="2">${escape(registrationCode)}</td>
        </tr>
        <tr>
          <td rowspan="3">3</td>
          <td rowspan="3">T-GNA Request</td>
          <td class="center" colspan="2">Date</td>
          <td class="center" colspan="2">Hours</td>
          <td class="center"></td>
        </tr>
        <tr>
          <td class="center">From</td>
          <td class="center">To</td>
          <td class="center">From</td>
          <td class="center">To</td>
          <td>MW</td>
        </tr>
        <tr>
          <td class="center">${escape(this.formatDateForPdf(record.validFromRaw))}</td>
          <td class="center">${escape(this.formatDateForPdf(record.validToRaw))}</td>
          <td class="center">${escape(record.hoursFrom || '-')}</td>
          <td class="center">${escape(record.hoursTo || '-')}</td>
          <td class="center">${escape(record.quantum || '-')}</td>
        </tr>
        <tr>
          <td rowspan="2">4</td>
          <td rowspan="2">Name of the entity</td>
          <td colspan="2" class="center">Injecting entity (mandatory for Exigency T-GNA application)</td>
          <td colspan="3" class="center">Drawee entity</td>
        </tr>
        <tr>
          <td colspan="2" class="center">${escape(record.seller)}</td>
          <td colspan="3" class="center" style="height: 30px;">${escape(record.buyer)}</td>
        </tr>
        <tr>
          <td>5</td>
          <td>Injection region</td>
          <td colspan="5">${escape(injectionRegion)}</td>
        </tr>
        <tr>
          <td>6</td>
          <td>Route</td>
          <td colspan="5">${escape(corridor)}</td>
        </tr>
        <tr>
          <td>7</td>
          <td colspan="3">Entity in which it is embedded</td>
          <td>(Yes/No)</td>
          <td colspan="2"></td>
        </tr>
        <tr>
          <td>8</td>
          <td colspan="3">Whether the transaction under GTAM</td>
          <td>(Yes/No)</td>
          <td colspan="2">No</td>
        </tr>
        <tr>
          <td>9</td>
          <td colspan="4">Source of generation is solar / non-solar / hydro<br>(applicable in case of Exigency T-GNA application)</td>
          <td colspan="2">T-GNA</td>
        </tr>
        <tr>
          <td>10</td>
          <td colspan="3">Granting T-GNA/T-GNARE application in part quantum and part period or both in case of constraints as per available transmission capability</td>
          <td>(Yes/No)</td>
          <td colspan="2"></td>
        </tr>
      </table>

      <div class="declaration">
        <b>Declaration:</b>
        <ol>
          <li>The provisions of the Electricity Act 2003, Indian Electricity Grid Code and all applicable CERC regulations with respect to T-GNA/T-GNARE transactions in interstate transmission, as amended from time to time are hereby understood and shall be binding.</li>
          <li>Necessary infrastructure for time-block wise metering and accounting in accordance with the provisions of the Grid code and appropriate communication system in accordance with the provisions of the Communication Regulations are in place for the point of drawal and point of injection, if available.</li>
          <li>The Nodal Agency is indemnified at all times from any and all claims, actions and all other obligations by or to third parties arising out of or resulting from the transactions under T-GNA/T-GNARE.</li>
          <li>There is a valid contract for the proposed scheduling.</li>
        </ol>
      </div>

      <div class="signature-row">
        <div class="date-block">Date: ${escape(todayDate)}</div>
        <div class="signature-block">
          <div>Name: Adarsh Kumar</div>
          <div>Designation: Team Lead</div>
        </div>
      </div>
    </div>
  </div>
  <script>
    window.addEventListener('load', function () {
      setTimeout(function () {
        window.print();
      }, 300);
    });
  </script>
</body>
</html>`;
  }

  private formatDateForPdf(dateText: string | undefined): string {
    if (!dateText) return '-';
    const normalized = String(dateText).slice(0, 10);
    const [yyyy, mm, dd] = normalized.split('-');
    if (!yyyy || !mm || !dd) return dateText;
    return `${dd}-${mm}-${yyyy}`;
  }

  private escapeHtml(value: string): string {
    return value
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }
}
